import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Check, ChevronDown, Plus, Search, PackagePlus } from 'lucide-react';
import { useUIColors } from '../../hooks/useTheme';
import { Dialog } from '../reusable';
import { SearchableSelect } from '../forms/SearchableSelect';

const EMPTY_LEAD_LABEL = 'Sin lead';

const resolveLeadName = (row) => {
  const name = String(row?.organization_name || '').trim();
  return name || EMPTY_LEAD_LABEL;
};

const ITEM_STATUS_STYLES = {
  Activo: { bg: '#dcfce7', text: '#166534', border: '#86efac' },
  Inactivo: { bg: '#e5e7eb', text: '#374151', border: '#d1d5db' },
  default: { bg: '#e5e7eb', text: '#374151', border: '#d1d5db' },
};

const formatMoney = (value) => {
  const n = Number(value || 0);
  return `$ ${n.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
};

const formatQty = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('es-CL', { maximumFractionDigits: 2 });
};

const renderStatusBadge = (value) => {
  const status = String(value || 'Activo').trim() || 'Activo';
  const palette = ITEM_STATUS_STYLES[status] || ITEM_STATUS_STYLES.default;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '64px',
        padding: '2px 7px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 700,
        background: palette.bg,
        color: palette.text,
        border: `1px solid ${palette.border}`,
      }}
    >
      {status}
    </span>
  );
};

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'Activo', label: 'Activo' },
  { value: 'Inactivo', label: 'Inactivo' },
];

const TABLE_COLUMNS = [
  { key: 'Cotización', align: 'left', sortKey: 'quotation_code' },
  { key: 'Lead', align: 'left', sortKey: 'organization_name' },
  { key: 'Producto', align: 'left', sortKey: 'item_name' },
  { key: 'Color / formato', align: 'left', sortKey: 'color' },
  { key: 'Cantidad', align: 'right', sortKey: 'quantity' },
  { key: 'Precios', align: 'right', sortKey: 'unit_price' },
  { key: 'Total', align: 'right', sortKey: 'line_total' },
  { key: 'Estado', align: 'center', sortKey: 'status' },
];

const getSortValue = (row, sortKey) => {
  switch (sortKey) {
    case 'quotation_code':
      return String(row.quotation_code || row.quotation_id || '').toLowerCase();
    case 'organization_name':
      return String(row.organization_name || '').toLowerCase();
    case 'item_name':
      return String(row.item_name || row.product_name || '').toLowerCase();
    case 'color':
      return `${row.color || ''} ${row.unit || ''}`.toLowerCase();
    case 'quantity':
      return Number(row.quantity) || 0;
    case 'unit_price':
      return Number(row.unit_price) || 0;
    case 'line_total':
      return Number(row.line_total) || 0;
    case 'status':
      return String(row.status || '').toLowerCase();
    default:
      return '';
  }
};

const compareRows = (a, b, sortKey, sortDir) => {
  const mult = sortDir === 'asc' ? 1 : -1;
  const va = getSortValue(a, sortKey);
  const vb = getSortValue(b, sortKey);
  if (typeof va === 'number' && typeof vb === 'number') {
    return (va - vb) * mult;
  }
  return String(va).localeCompare(String(vb), 'es', { numeric: true, sensitivity: 'base' }) * mult;
};

/**
 * Tabla de ítems de cotización: mismo look & feel que QuotationsTable3 / Table3.
 */
const QuotationItemsTable3 = ({
  rows = [],
  loading = false,
  error = '',
  isDarkMode = false,
  onAdd,
  onRowOpen,
  onBulkDelete,
  onBulkAssignProduct,
  productOptions = [],
}) => {
  const ui = useUIColors();
  void isDarkMode;

  const bg = ui.page;
  const headerBg = ui.header;
  const border = ui.border;
  const text = ui.text;
  const muted = ui.muted;
  const rowHover = ui.rowHover;
  const padV = 9;
  const padH = 10;
  const thPadV = 8;
  const inputBg = ui.inputBg;
  const inputBorder = ui.inputBorder;
  const panel = ui.panel || bg;
  const activeBg = `color-mix(in srgb, ${ui.interactive || '#2563eb'} 14%, ${panel})`;

  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterQuotation, setFilterQuotation] = useState('');
  const [leadVisibility, setLeadVisibility] = useState({});
  const [leadMenuOpen, setLeadMenuOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState('quotation_code');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkPending, setBulkPending] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignProductSku, setAssignProductSku] = useState('');
  const [assignError, setAssignError] = useState('');
  const headerSelectRef = useRef(null);
  const leadMenuRef = useRef(null);

  const showBulkChrome = Boolean(onBulkDelete || onBulkAssignProduct);
  const colCount = showBulkChrome ? TABLE_COLUMNS.length + 1 : TABLE_COLUMNS.length;

  const quotationFilterOptions = useMemo(() => {
    const map = new Map();
    (rows || []).forEach((row) => {
      const code = String(row.quotation_code || '').trim();
      if (!code) return;
      map.set(code, code);
    });
    return Array.from(map.values())
      .sort((a, b) => a.localeCompare(b, 'es', { numeric: true }))
      .map((code) => ({ value: code, label: code }));
  }, [rows]);

  const leadFilterOptions = useMemo(() => {
    const set = new Set();
    (rows || []).forEach((row) => set.add(resolveLeadName(row)));
    return Array.from(set).sort((a, b) => {
      if (a === EMPTY_LEAD_LABEL) return 1;
      if (b === EMPTY_LEAD_LABEL) return -1;
      return a.localeCompare(b, 'es', { sensitivity: 'base' });
    });
  }, [rows]);

  useEffect(() => {
    if (!leadFilterOptions.length) return;
    setLeadVisibility((prev) => {
      let changed = false;
      const next = { ...prev };
      leadFilterOptions.forEach((lead) => {
        if (next[lead] === undefined) {
          next[lead] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [leadFilterOptions]);

  useEffect(() => {
    if (!leadMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (leadMenuRef.current && !leadMenuRef.current.contains(event.target)) {
        setLeadMenuOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setLeadMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [leadMenuOpen]);

  const visibleLeadCount = leadFilterOptions.filter((lead) => leadVisibility[lead] !== false).length;
  const hasLeadFilterRestriction =
    leadFilterOptions.length > 0 && visibleLeadCount < leadFilterOptions.length;

  const filteredRows = useMemo(() => {
    let list = rows || [];
    const q = searchQuery.trim().toLowerCase();
    const tokens = q.split(/\s+/).filter(Boolean);
    if (tokens.length) {
      list = list.filter((row) => {
        const hay = [
          row.quotation_code,
          row.quotation_id,
          row.item_code,
          row.product_sku,
          row.item_name,
          row.product_name,
          row.color,
          row.color_code,
          row.quantity,
          row.unit,
          row.unit_price,
          row.discount_percent,
          row.line_total,
          row.status,
          row.organization_name,
        ]
          .filter((x) => x !== undefined && x !== null)
          .map((x) => String(x))
          .join(' ')
          .toLowerCase();
        return tokens.every((t) => hay.includes(t));
      });
    }
    if (filterStatus) {
      list = list.filter((row) => String(row.status || 'Activo').trim() === filterStatus);
    }
    if (filterQuotation) {
      list = list.filter((row) => String(row.quotation_code || '').trim() === filterQuotation);
    }
    if (hasLeadFilterRestriction) {
      list = list.filter((row) => leadVisibility[resolveLeadName(row)] !== false);
    }
    return list;
  }, [
    rows,
    searchQuery,
    filterStatus,
    filterQuotation,
    hasLeadFilterRestriction,
    leadVisibility,
  ]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => compareRows(a, b, sortColumn, sortDir));
    return arr;
  }, [filteredRows, sortColumn, sortDir]);

  const visibleIds = useMemo(
    () => sortedRows.map((r) => Number(r.id)).filter((id) => Number.isFinite(id)),
    [sortedRows]
  );

  const allVisibleSelected =
    showBulkChrome && visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = showBulkChrome && visibleIds.some((id) => selectedIds.includes(id));

  useEffect(() => {
    const el = headerSelectRef.current;
    if (!el || el.type !== 'checkbox') return;
    el.indeterminate = someVisibleSelected && !allVisibleSelected;
  }, [someVisibleSelected, allVisibleSelected, showBulkChrome]);

  const toggleRowSelected = (rawId) => {
    const id = Number(rawId);
    if (!Number.isFinite(id)) return;
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleSelectAllVisible = () => {
    setSelectedIds((prev) => {
      if (visibleIds.length && visibleIds.every((id) => prev.includes(id))) {
        return prev.filter((id) => !visibleIds.includes(id));
      }
      const next = new Set(prev);
      visibleIds.forEach((id) => next.add(id));
      return Array.from(next);
    });
  };

  const clearSelection = () => setSelectedIds([]);

  const handleSortClick = (key) => {
    if (sortColumn === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDir('asc');
    }
  };

  const handleToggleLead = (lead) => {
    setLeadVisibility((prev) => ({
      ...prev,
      [lead]: prev[lead] === false,
    }));
  };

  const handleShowAllLeads = () => {
    setLeadVisibility(
      leadFilterOptions.reduce((acc, lead) => {
        acc[lead] = true;
        return acc;
      }, {})
    );
  };

  const handleHideAllLeads = () => {
    setLeadVisibility(
      leadFilterOptions.reduce((acc, lead) => {
        acc[lead] = false;
        return acc;
      }, {})
    );
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterStatus('');
    setFilterQuotation('');
    handleShowAllLeads();
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete || selectedIds.length === 0) return;
    const ok = window.confirm(
      `¿Eliminar ${selectedIds.length} ítem(s) seleccionado(s)?\n\nSe dará de baja del listado (soft delete).`
    );
    if (!ok) return;
    setBulkPending(true);
    try {
      const selectedRows = (rows || []).filter((r) => selectedIds.includes(Number(r.id)));
      await onBulkDelete(selectedRows);
      clearSelection();
    } catch (e) {
      window.alert(e?.message || 'No se pudieron eliminar los ítems.');
    } finally {
      setBulkPending(false);
    }
  };

  const openAssignModal = () => {
    if (!onBulkAssignProduct || selectedIds.length === 0) return;
    setAssignProductSku('');
    setAssignError('');
    setShowAssignModal(true);
  };

  const handleConfirmAssignProduct = async () => {
    if (!onBulkAssignProduct || selectedIds.length === 0) return;
    const sku = String(assignProductSku || '').trim();
    if (!sku) {
      setAssignError('Selecciona un producto / SKU del catálogo.');
      return;
    }
    const catalogOption =
      (productOptions || []).find((option) => String(option.value) === sku) || null;
    setBulkPending(true);
    setAssignError('');
    try {
      const selectedRows = (rows || []).filter((r) => selectedIds.includes(Number(r.id)));
      await onBulkAssignProduct(selectedRows, {
        product_sku: sku,
        item_name: String(catalogOption?.name || catalogOption?.label || '').trim(),
        unit_price: catalogOption?.unit_price,
        kind: catalogOption?.kind || '',
      });
      setShowAssignModal(false);
      setAssignProductSku('');
      clearSelection();
    } catch (e) {
      setAssignError(e?.message || 'No se pudo asignar el producto a los ítems.');
    } finally {
      setBulkPending(false);
    }
  };

  const assignProductOptions = useMemo(
    () => (productOptions || []).filter((option) => String(option.value || '').trim()),
    [productOptions]
  );

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    Boolean(filterStatus) ||
    Boolean(filterQuotation) ||
    hasLeadFilterRestriction;
  const subtitle = loading
    ? 'Cargando…'
    : error ||
      (hasActiveFilters
        ? `Mostrando ${sortedRows.length} de ${rows.length} ítems · A005_quotation_items`
        : `${rows.length} ítems · A005_quotation_items`);

  const selectStyle = {
    height: '36px',
    padding: '0 10px',
    borderRadius: '8px',
    border: `1px solid ${inputBorder}`,
    background: inputBg,
    color: text,
    fontSize: '13px',
    minWidth: '160px',
    outline: 'none',
  };

  return (
    <div
      style={{
        border: `1px solid ${border}`,
        borderRadius: '12px',
        overflow: 'hidden',
        background: bg,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: `1px solid ${border}`,
          background: headerBg,
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: text }}>Items de Cotización</h3>
          <p style={{ margin: 0, fontSize: '12px', color: muted }}>{subtitle}</p>
        </div>
        {onAdd ? (
          <button
            type="button"
            onClick={onAdd}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '36px',
              padding: '0 14px',
              borderRadius: '8px',
              border: 'none',
              background: '#16a34a',
              color: '#fff',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            <Plus size={16} /> Nuevo ítem
          </button>
        ) : null}
      </div>

      <div
        style={{
          padding: '10px 16px',
          borderBottom: `1px solid ${border}`,
          background: bg,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'flex-end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 220px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Buscar
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Search size={16} color={muted} aria-hidden />
            <input
              type="search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cotización, lead, SKU, producto, color…"
              style={{ ...selectStyle, flex: 1, minWidth: 0 }}
            />
          </span>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '160px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Cotización
          </span>
          <select
            value={filterQuotation}
            onChange={(e) => setFilterQuotation(e.target.value)}
            style={selectStyle}
          >
            <option value="">Todas</option>
            {quotationFilterOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {leadFilterOptions.length > 0 ? (
          <div ref={leadMenuRef} style={{ position: 'relative', flex: '0 0 auto', minWidth: '180px' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
                Lead
              </span>
              <button
                type="button"
                onClick={() => setLeadMenuOpen((prev) => !prev)}
                style={{
                  ...selectStyle,
                  minWidth: '180px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '6px',
                  cursor: 'pointer',
                }}
              >
                <span>
                  {visibleLeadCount === leadFilterOptions.length
                    ? 'Todos'
                    : `${visibleLeadCount} de ${leadFilterOptions.length}`}
                </span>
                <ChevronDown size={14} aria-hidden />
              </button>
            </label>
            {leadMenuOpen ? (
              <div
                role="menu"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '4px',
                  minWidth: '240px',
                  maxWidth: '320px',
                  maxHeight: '280px',
                  overflowY: 'auto',
                  background: panel,
                  border: `1px solid ${border}`,
                  borderRadius: '8px',
                  boxShadow: ui.shadowMd || '0 8px 24px rgba(0,0,0,0.12)',
                  zIndex: 40,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '8px',
                    padding: '6px 10px',
                    borderBottom: `1px solid ${border}`,
                    background: headerBg,
                  }}
                >
                  <button
                    type="button"
                    onClick={handleShowAllLeads}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: ui.interactive || '#2563eb',
                      fontSize: '10px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    Todas
                  </button>
                  <button
                    type="button"
                    onClick={handleHideAllLeads}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: muted,
                      fontSize: '10px',
                      fontWeight: 700,
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    Ninguna
                  </button>
                </div>
                {leadFilterOptions.map((lead) => {
                  const checked = leadVisibility[lead] !== false;
                  return (
                    <label
                      key={lead}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '7px 10px',
                        fontSize: '12px',
                        fontWeight: 600,
                        color: text,
                        cursor: 'pointer',
                        background: checked ? activeBg : panel,
                        borderBottom: `1px solid color-mix(in srgb, ${border} 70%, transparent)`,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => handleToggleLead(lead)}
                        style={{ margin: 0, accentColor: ui.interactive || '#2563eb' }}
                      />
                      <span style={{ flex: 1 }}>{lead}</span>
                      {checked ? <Check size={14} color={ui.interactive || '#2563eb'} /> : null}
                    </label>
                  );
                })}
              </div>
            ) : null}
          </div>
        ) : null}
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '150px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, color: muted, letterSpacing: '0.04em' }}>
            Estado
          </span>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            style={selectStyle}
          >
            {STATUS_FILTER_OPTIONS.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        {hasActiveFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            style={{
              height: '36px',
              padding: '0 12px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: muted,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: '2px',
            }}
          >
            Limpiar filtros
          </button>
        ) : null}
      </div>

      {showBulkChrome && selectedIds.length > 0 ? (
        <div
          style={{
            padding: '10px 16px',
            borderBottom: `1px solid ${border}`,
            background: `color-mix(in srgb, ${ui.interactive || '#2563eb'} 8%, ${bg})`,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '10px',
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: '13px', fontWeight: 600, color: text }}>
            {selectedIds.length} seleccionado{selectedIds.length === 1 ? '' : 's'}
          </span>
          <button
            type="button"
            onClick={clearSelection}
            disabled={bulkPending}
            style={{
              height: '32px',
              padding: '0 10px',
              borderRadius: '8px',
              border: `1px solid ${inputBorder}`,
              background: inputBg,
              color: muted,
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Limpiar selección
          </button>
          {onBulkAssignProduct ? (
            <button
              type="button"
              onClick={openAssignModal}
              disabled={bulkPending}
              style={{
                height: '32px',
                padding: '0 12px',
                borderRadius: '8px',
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: bulkPending ? 'wait' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <PackagePlus size={14} aria-hidden />
              Asignar
            </button>
          ) : null}
          {onBulkDelete ? (
            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={bulkPending}
              style={{
                height: '32px',
                padding: '0 12px',
                borderRadius: '8px',
                border: 'none',
                background: ui.danger || '#dc2626',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: bulkPending ? 'wait' : 'pointer',
                opacity: bulkPending ? 0.7 : 1,
              }}
            >
              {bulkPending ? 'Eliminando…' : 'Eliminar'}
            </button>
          ) : null}
        </div>
      ) : null}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
          <thead>
            <tr style={{ background: headerBg }}>
              {showBulkChrome ? (
                <th
                  style={{
                    width: 42,
                    padding: `${thPadV}px ${padH}px`,
                    borderBottom: `1px solid ${border}`,
                    textAlign: 'center',
                  }}
                >
                  <input
                    ref={headerSelectRef}
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                    aria-label="Seleccionar todos los visibles"
                  />
                </th>
              ) : null}
              {TABLE_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  onClick={() => (col.sortKey ? handleSortClick(col.sortKey) : undefined)}
                  style={{
                    textAlign: col.align || 'left',
                    padding: `${thPadV}px ${padH}px`,
                    fontSize: '10px',
                    fontWeight: 700,
                    letterSpacing: '0.04em',
                    color: muted,
                    borderBottom: `1px solid ${border}`,
                    cursor: col.sortKey ? 'pointer' : 'default',
                    userSelect: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      width: col.align === 'center' || col.align === 'right' ? '100%' : 'auto',
                      justifyContent:
                        col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start',
                    }}
                  >
                    {col.key}
                    {col.sortKey ? (
                      sortColumn === col.sortKey ? (
                        sortDir === 'asc' ? (
                          <ArrowUp size={13} strokeWidth={2.5} aria-hidden />
                        ) : (
                          <ArrowDown size={13} strokeWidth={2.5} aria-hidden />
                        )
                      ) : (
                        <ArrowUpDown size={13} strokeWidth={2} style={{ opacity: 0.4 }} aria-hidden />
                      )
                    ) : null}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  Cargando ítems…
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: '28px', textAlign: 'center', color: muted }}>
                  {error ||
                    (hasActiveFilters
                      ? 'Ningún resultado con los filtros actuales.'
                      : 'No hay ítems. Usa «Nuevo ítem» para agregar.')}
                </td>
              </tr>
            ) : (
              sortedRows.map((row) => {
                const selected = selectedIds.includes(Number(row.id));
                return (
                  <tr
                    key={row.id}
                    onClick={() => onRowOpen?.(row)}
                    style={{
                      borderBottom: `1px solid ${border}`,
                      cursor: onRowOpen ? 'pointer' : 'default',
                      background: selected
                        ? `color-mix(in srgb, ${ui.interactive || '#2563eb'} 10%, ${bg})`
                        : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (!selected) e.currentTarget.style.background = rowHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = selected
                        ? `color-mix(in srgb, ${ui.interactive || '#2563eb'} 10%, ${bg})`
                        : 'transparent';
                    }}
                  >
                    {showBulkChrome ? (
                      <td
                        style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => toggleRowSelected(row.id)}
                          aria-label={`Seleccionar ítem ${row.item_code || row.id}`}
                        />
                      </td>
                    ) : null}
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'top' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: text }}>
                        {row.quotation_code || `ID ${row.quotation_id || '—'}`}
                      </div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: 2 }}>
                        {row.item_code || 'Sin código'}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'top', maxWidth: 220 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: text }}>
                        {resolveLeadName(row)}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'top', maxWidth: 280 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: text }}>
                        {row.item_name || row.product_name || '—'}
                      </div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: 2 }}>
                        {row.product_sku || 'Sin SKU'}
                      </div>
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, verticalAlign: 'top' }}>
                      <div style={{ fontSize: '13px', color: text }}>{row.color || '—'}</div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: 2 }}>
                        {row.unit || '—'}
                        {row.color_code ? ` · ${row.color_code}` : ''}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        textAlign: 'right',
                        fontSize: '13px',
                        fontWeight: 600,
                        color: text,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatQty(row.quantity)}
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        textAlign: 'right',
                        verticalAlign: 'top',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      <div style={{ fontSize: '13px', fontWeight: 600, color: text }}>
                        {formatMoney(row.unit_price)}
                      </div>
                      <div style={{ fontSize: '11px', color: muted, marginTop: 2 }}>
                        Desc. {Number(row.discount_percent || 0).toLocaleString('es-CL')}%
                      </div>
                    </td>
                    <td
                      style={{
                        padding: `${padV}px ${padH}px`,
                        textAlign: 'right',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: text,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {formatMoney(row.line_total)}
                    </td>
                    <td style={{ padding: `${padV}px ${padH}px`, textAlign: 'center' }}>
                      {renderStatusBadge(row.status)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Dialog
        isOpen={showAssignModal}
        onClose={() => {
          if (!bulkPending) setShowAssignModal(false);
        }}
        title="Asignar producto a ítems"
        subtitle={
          selectedIds.length === 1
            ? 'Elige un producto / SKU del catálogo de pinturas o upselling.'
            : `Asigna el mismo producto / SKU a ${selectedIds.length} ítems seleccionados.`
        }
        size="md"
        isDarkMode={isDarkMode}
        zIndex={10050}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', padding: '4px 0' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>
              Producto / SKU
            </span>
            <SearchableSelect
              value={assignProductSku}
              options={assignProductOptions}
              onChange={(nextValue) => {
                setAssignProductSku(String(nextValue || ''));
                setAssignError('');
              }}
              placeholder="Escribe para filtrar por nombre o SKU…"
              disabled={bulkPending}
              isDarkMode={isDarkMode}
              maxResults={40}
              style={{
                ...selectStyle,
                height: '40px',
                width: '100%',
                minWidth: 0,
                boxSizing: 'border-box',
              }}
              dropdownStyle={{ maxHeight: 240, zIndex: 30 }}
            />
          </label>

          {assignError ? (
            <p style={{ margin: 0, fontSize: 13, color: ui.danger || '#dc2626' }}>{assignError}</p>
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button
              type="button"
              onClick={() => setShowAssignModal(false)}
              disabled={bulkPending}
              style={{
                height: 36,
                padding: '0 14px',
                borderRadius: 8,
                border: `1px solid ${inputBorder}`,
                background: inputBg,
                color: text,
                fontWeight: 600,
                fontSize: 13,
                cursor: bulkPending ? 'wait' : 'pointer',
              }}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleConfirmAssignProduct}
              disabled={bulkPending || !String(assignProductSku || '').trim()}
              style={{
                height: 36,
                padding: '0 14px',
                borderRadius: 8,
                border: 'none',
                background: '#2563eb',
                color: '#fff',
                fontWeight: 700,
                fontSize: 13,
                cursor:
                  bulkPending || !String(assignProductSku || '').trim() ? 'not-allowed' : 'pointer',
                opacity: bulkPending || !String(assignProductSku || '').trim() ? 0.7 : 1,
              }}
            >
              {bulkPending ? 'Asignando…' : 'Asignar producto'}
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default QuotationItemsTable3;
